import {
  putAdviseSessionInCache,
  updateAdviseSessionInCache,
} from 'hooks/adviseSessions';
import api from 'services/Api';
import instantSession from 'services/InstantSession';
import {pauseRingtone} from '../../views/extera/ringtonePlayer';

export function createAdviseSession(data) {
  return api.invoke('POST', '/core/v1/adviseSessions', data);
}

export async function fetchAdviseSessionStatus(id) {
  const {data} = await api.invoke(
    'GET',
    `/core/v1/adviseSessions/${id}/status`,
  );
  if (data.adviseSession) {
    putAdviseSessionInCache(data.adviseSession);
  }
  return data;
}

export async function acceptAdviseSession(id) {
  pauseRingtone();
  const {data} = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/accept`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
  putAdviseSessionInCache(data.adviseSession);
  window[`callEpoch${id}`] = Date.now();
}

export async function rejectAdviseSession(id) {
  pauseRingtone();
  await api.invoke('POST', `/core/v1/adviseSessions/${id}/reject`, undefined, {
    api: {
      emitNotification: true,
    },
  });
  updateAdviseSessionInCache(id, {
    status: 'REJECTED',
  });
  instantSession.resumeAssignMe();
}

export function archiveAdviseSession(id) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/archive`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export async function cancelAdviseSession(id) {
  await api.invoke('POST', `/core/v1/adviseSessions/${id}/cancel`, undefined, {
    api: {
      emitNotification: true,
    },
  });
  updateAdviseSessionInCache(id, {
    status: 'CANCELED',
  });
  instantSession.resumeAssignMe();
}

export async function closeAdviseSession(id) {
  await api.invoke('POST', `/core/v1/adviseSessions/${id}/close`, undefined, {
    api: {
      emitNotification: true,
    },
  });
  updateAdviseSessionInCache(id, {
    status: 'CLOSED',
  });
  setTimeout(instantSession.resumeAssignMe, 60 * 1000);
}

export function dialAdviser(id) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/dialAdviser`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export async function addComment(id, data) {
  const response = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/comments`,
    data,
  );
  updateAdviseSessionInCache(id, response.data);
}

export async function addRecommendation(id, data) {
  const response = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/recommendations`,
    data,
  );
  updateAdviseSessionInCache(id, response.data);
}

export async function extendSession(id, data) {
  const response = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/extends`,
    data,
  );
  return response.data;
}

export async function acceptExtendSession(id, extendId) {
  const {data} = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/extends/${extendId}/accept`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
  putAdviseSessionInCache(data.adviseSession);
}

export function rejectExtendSession(id, extendId) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/extends/${extendId}/reject`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export async function fetchExtendSessionStatus(id, extendId) {
  const {data} = await api.invoke(
    'GET',
    `/core/v1/adviseSessions/${id}/extends/${extendId}/status`,
  );
  if (data.adviseSession) {
    putAdviseSessionInCache(data.adviseSession);
  }

  return data;
}

export function fetchSignalingConfiguration() {
  return api.invoke('GET', '/core/v1/adviseSessions/signalingConfiguration');
}

export function signalingOffer(id, data) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/signaling/offer`,
    data,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export function signalingAccept(id, signalingId, data) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/signaling/${signalingId}/accept`,
    data,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export function signalingCandidate(id, signalingId, data) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/signaling/${signalingId}/candidate`,
    data,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export function voipDial(id) {
  return api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/voip/dial`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
}

export async function uploadFile(id, fileType, file, options) {
  const formData = new FormData();
  formData.set('file', file);
  const {data} = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/upload/${fileType}`,
    formData,
    {
      api: {
        emitNotification: true,
      },
      ...options,
    },
  );
  return data;
}

export function entry(id, entry) {
  return api.invoke('POST', `/core/v1/adviseSessions/${id}/entry`, {entry});
}

export async function fetchAdviseSessionUserInfo(id) {
  const {data} = await api.invoke(
    'GET',
    `/core/v1/adviseSessions/${id}/userInfo`,
    undefined,
    {
      api: {
        emitNotification: true,
      },
    },
  );
  return data;
}

export async function fetchAdviseSession(id) {
  const {data} = await api.invoke('GET', `/core/v1/adviseSessions/${id}`);
  return data;
}

export async function fetchAdviseSessions(params) {
  const {data} = await api.invoke('GET', '/core/v1/adviseSessions', undefined, {
    params,
    api: {
      emitNotification: true,
    },
  });

  for (const adviseSession of data.adviseSessions) {
    putAdviseSessionInCache(adviseSession);
  }
  return data;
}

export async function fetchAdviseSessionRequisitions(params) {
  const {data} = await api.invoke(
    'GET',
    '/core/v1/adviseSessions/requisitions',
    undefined,
    {
      params,
      api: {
        emitNotification: true,
      },
    },
  );
  for (const adviseSession of data.adviseSessions) {
    putAdviseSessionInCache(adviseSession);
  }

  return data;
}

export async function checkDiscountCode(data) {
  const {data: resData} = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/checkDiscountCode`,
    data,
  );
  return resData;
}

export async function sendVetReview(id, params) {
  const {data} = await api.invoke(
    'POST',
    `/core/v1/adviseSessions/${id}/vetReview`,
    params,
  );
  return data;
}

export async function getVetCategories() {
  const {
    data: {vetCategories},
  } = await api.invoke('GET', '/core/v1/adviseSessions/vetCategories');
  sortWithOther(vetCategories);
  return vetCategories;
}

export async function getAnimalList(id) {
  const {
    data: {animals},
  } = await api.invoke(
    'GET',
    `/core/v1/adviseSessions/vetCategories/${id}/animals`,
  );
  sortWithOther(animals);
  return animals;
}

function sortWithOther(list) {
  for (let i = 0; i < list.length; i++) {
    if (list[i].name === 'سایر') {
      if (i < list.length - 1) {
        list.push(list.splice(i, 1)[0]);
      }
      break;
    }
  }
}

export async function getReserveTime(adviserId) {
  const {
    data: {adviseSessions},
  } = await api.invoke(
    'POST',
    '/core/v1/adviseSessions/requisitions/' + adviserId,
  );
  const {
    data: {timetable},
  } = await api.invoke(
    'GET',
    `/core/v1/advisers/${adviserId}/timetable`,
    undefined,
    {params: {type: 'ONLINE_ADVISING'}},
  );
  return {adviseSessions, timetable};
}

export async function getCompanyPercentDiscount(params) {
  const {data} = await api.invoke(
    'GET',
    `/core/v1/adviseSessions/partner/status`,
    undefined,
    {params},
  );

  return data.percent;
}

export async function sendReservationByAdviser(params) {
  const {data} = await api.invoke(
    'POST',
    `/core/v1/advisers/reservation`,
    params,
  );
  return data;
}

export async function sendReservationUserStatus(params) {
  const {data} = await api.invoke('POST', `/core/v1/users/reservation`, params);

  return data;
}
